import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Avatar,
  Stack,
  Tooltip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import LowPriorityIcon from '@mui/icons-material/LowPriority';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import BusinessIcon from '@mui/icons-material/Business';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { DensityMedium } from '@mui/icons-material';
import { TaskDetailsDialog } from './TaskDialogs';
import EditTask from './EditTask';
import { deleteTask, getCreatedTasks } from '../../redux/features/taskManagementSlice';

// ------------------ CONFIG MAPS ------------------
const priorityConfig = {
  1: {
    label: 'Critical',
    icon: <DragHandleIcon sx={{ fontSize: 14 }} />,
    color: '#1D4ED8',
    bg: '#EFF6FF',
    border: '#93C5FD',
  },
  2: {
    label: 'High',
    icon: <PriorityHighIcon sx={{ fontSize: 14 }} />,
    color: '#B91C1C',
    bg: '#FEF2F2',
    border: '#FCA5A5',
  },
  3: {
    label: 'Medium',
    icon: <DensityMedium sx={{ fontSize: 14 }} />,
    color: '#D97706',
    bg: '#FFFBEB',
    border: '#FCD34D',
  },
  4: {
    label: 'Low',
    icon: <LowPriorityIcon sx={{ fontSize: 14 }} />,
    color: '#15803D',
    bg: '#F0FDF4',
    border: '#86EFAC',
  },
};

const assignmentStatusConfig = {
  Pending: {
    icon: <HourglassEmptyIcon sx={{ fontSize: 14 }} />,
    color: '#C2410C',
    bg: '#FFF7ED',
    border: '#FDBA74',
  },
  Assigned: {
    icon: <AssignmentIndIcon sx={{ fontSize: 14 }} />,
    color: '#6D28D9',
    bg: '#F5F3FF',
    border: '#C4B5FD',
  },
  'In Progress': {
    icon: <AutorenewIcon sx={{ fontSize: 14 }} />,
    color: '#1D4ED8',
    bg: '#EFF6FF',
    border: '#93C5FD',
  },
  'On Hold': {
    icon: <AccessTimeIcon sx={{ fontSize: 14 }} />,
    color: '#92400E',
    bg: '#FFFBEB',
    border: '#FCD34D',
  },
  Rejected: {
    icon: <CancelIcon sx={{ fontSize: 14 }} />,
    color: '#B91C1C',
    bg: '#FEF2F2',
    border: '#FCA5A5',
  },
  Completed: {
    icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
    color: '#15803D',
    bg: '#F0FDF4',
    border: '#86EFAC',
  },
  Cancelled: {
    icon: <CancelIcon sx={{ fontSize: 14 }} />,
    color: '#991B1B',
    bg: '#FEF2F2',
    border: '#F87171',
  },
};

// Avatar gradients (cycles through)
const avatarGradients = [
  'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
  'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
  'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
  'linear-gradient(135deg, #f59e0b 0%, #fb923c 100%)',
];

const getInitials = (name = '') =>
  name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// ------------------ COMPONENT ------------------
const MyTasksTable = ({ filters }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { createdTasks, taskCategories, deleteLoading } = useSelector((state) => state.taskManagement);
  const data = createdTasks || [];

  // default categories fallback (from user)
  const defaultCategories = [
    { task_category_id: 1, task_category_name: 'All' },
  ];

  const categories = taskCategories && taskCategories.length ? taskCategories : defaultCategories;

  const [categoryFilter, setCategoryFilter] = useState('');
  const [assignFilter, setAssignFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, task: null });

  const handleView = (task) => {
    setSelectedTask(task);
    setOpenDialog(true);
  };

  const handleEdit = (task) => {
    setSelectedTask(task);
    setOpenEditDialog(true);
  };

  const handleDeleteClick = (task) => {
    setConfirmDelete({ open: true, task });
  };

  const closeConfirmDelete = () => {
    if (deleteLoading) return;
    setConfirmDelete({ open: false, task: null });
  };

  const handleConfirmDelete = () => {
    const task = confirmDelete.task;
    if (!task) return;
    dispatch(deleteTask(task.task_id)).then((res) => {
      if (res?.payload?.status === 'success') {
        enqueueSnackbar(res.payload.message || 'Task deleted', { variant: 'success' });
        dispatch(getCreatedTasks(filters));
      } else {
        enqueueSnackbar(res?.payload?.message || 'Failed to delete task', { variant: 'error' });
      }
      setConfirmDelete({ open: false, task: null });
    });
  };

  const query = searchQuery.trim().toLowerCase();
  const filteredData = data
    // Category filter
    .filter((t) => (categoryFilter ? String(t.task_category_id) === String(categoryFilter) : true))
    // Assignment filter (Assigned / Unassigned)
    .filter((t) => {
      if (assignFilter === 'all') return true;
      const assigned =
        t?.task_assigned_to !== null && t?.task_assigned_to !== undefined && t?.task_assigned_to !== '';
      if (assignFilter === 'assigned') return assigned;
      if (assignFilter === 'unassigned') return !assigned;
      return true;
    })
    // Free-text search across title, people, department and task id
    .filter((t) =>
      query
        ? [t.title, t.assigned_to_name, t.assigned_by_name, t.target_dept_name, `#${t.task_id}`, t.task_id]
            .filter((v) => v !== null && v !== undefined)
            .some((v) => String(v).toLowerCase().includes(query))
        : true,
    );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, background: '#FFFFFF' }}>
      {/* Section Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2.5,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
            }}
          >
            <ListAltIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#1E293B',
                letterSpacing: '-0.3px',
                lineHeight: 1.2,
              }}
            >
              My Created Tasks
            </Typography>
            <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '0.78rem' }}>
              {filteredData?.length || 0} task{filteredData?.length !== 1 ? 's' : ''} shown
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 240, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: '#94A3B8' }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Filter: Assigned / Unassigned */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Assignment</InputLabel>
            <Select
              value={assignFilter}
              label="Assignment"
              onChange={(e) => setAssignFilter(e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="assigned">Assigned</MenuItem>
              <MenuItem value="unassigned">Unassigned</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => setCategoryFilter(e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="">All</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.task_category_id} value={cat.task_category_id}>
                  {cat.task_category_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Chip
            label={`Total: ${data?.length || 0}`}
            size="small"
            sx={{
              background: 'linear-gradient(135deg, #EEF2FF 0%, #F5F3FF 100%)',
              color: '#6366F1',
              fontWeight: 700,
              fontSize: '0.75rem',
              border: '1px solid #E0E7FF',
              height: 28,
            }}
          />
        </Stack>
      </Stack>

      {/* Table Container */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid #E2E8F0',
          overflow: 'auto',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          '&::-webkit-scrollbar': { height: 8, width: 8 },
          '&::-webkit-scrollbar-track': {
            background: '#F1F5F9',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#CBD5E1',
            borderRadius: 4,
            '&:hover': { background: '#94A3B8' },
          },
        }}
      >
        <Table sx={{ minWidth: 1100 }}>
          {/* Gradient Header */}
          <TableHead>
            <TableRow
              sx={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
                '& th': {
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: '0.78rem',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  borderBottom: 'none',
                  py: 2,
                  whiteSpace: 'nowrap',
                },
              }}
            >
              <TableCell sx={{ pl: 3, width: 60 }}>#</TableCell>
              <TableCell sx={{ minWidth: 280 }}>Task Title</TableCell>
              <TableCell sx={{ minWidth: 180 }}>Target Department</TableCell>
              <TableCell sx={{ minWidth: 180 }}>Assigned To</TableCell>
              <TableCell sx={{ minWidth: 180 }}>Assigned By</TableCell>
              <TableCell sx={{ minWidth: 130 }}>Priority</TableCell>
              <TableCell sx={{ minWidth: 150 }}>Due Date</TableCell>
              <TableCell sx={{ minWidth: 150 }}>Created On</TableCell>
              <TableCell sx={{ minWidth: 150 }}>Status</TableCell>
              <TableCell sx={{ pr: 3, width: 80, textAlign: 'center' }}>Action</TableCell>
            </TableRow>
          </TableHead>

          {/* Body */}
          <TableBody>
            {!filteredData || filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ py: 8, textAlign: 'center' }}>
                  <ListAltIcon sx={{ fontSize: 48, color: '#CBD5E1', mb: 1 }} />
                  <Typography variant="body2" sx={{ color: '#94A3B8', fontWeight: 600 }}>
                    You haven't created any tasks yet
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((task, index) => {
                const priority = priorityConfig[task.priority] || priorityConfig[1];
                const status = assignmentStatusConfig[task.assignment_status_name] || assignmentStatusConfig['Pending'];
                const isOverdue = new Date(task.expected_delivery_date) < new Date() && task.assignment_status !== 5;

                const assigneeGradient = avatarGradients[index % avatarGradients.length];

                return (
                  <TableRow
                    key={task.task_id}
                    sx={{
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: '#F8FAFC',
                      },
                      '& td': {
                        borderBottom: '1px solid #F1F5F9',
                        py: 2,
                      },
                      '&:last-child td': {
                        borderBottom: 'none',
                      },
                    }}
                  >
                    {/* # */}
                    <TableCell sx={{ pl: 3 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #F8FAFC 0%, #EEF2FF 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: '0.78rem',
                          color: '#6366F1',
                        }}
                      >
                        {index + 1}
                      </Box>
                    </TableCell>

                    {/* Task Title */}
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: '#1E293B',
                            fontSize: '0.86rem',
                            maxWidth: 280,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {task.title}
                        </Typography>
                        {isOverdue && (
                          <Tooltip title="Overdue" arrow>
                            <ErrorOutlineIcon sx={{ fontSize: 16, color: '#EF4444' }} />
                          </Tooltip>
                        )}
                      </Stack>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#94A3B8',
                          fontSize: '0.7rem',
                          display: 'block',
                          mt: 0.3,
                        }}
                      >
                        ID: #{task.task_id}
                      </Typography>
                    </TableCell>

                    {/* Target Department */}
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1.5,
                            background: '#F5F3FF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <BusinessIcon sx={{ fontSize: 16, color: '#8B5CF6' }} />
                        </Box>
                        <Typography
                          sx={{
                            fontSize: '0.83rem',
                            color: '#334155',
                            fontWeight: 500,
                          }}
                        >
                          {task.target_dept_name || '—'}
                        </Typography>
                      </Stack>
                    </TableCell>

                    {/* Assigned To */}
                    <TableCell>
                      {task.assigned_to_name ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar
                            sx={{
                              width: 30,
                              height: 30,
                              background: assigneeGradient,
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                            }}
                          >
                            {getInitials(task.assigned_to_name || '—')}
                          </Avatar>
                          <Typography
                            sx={{
                              fontSize: '0.83rem',
                              color: '#334155',
                              fontWeight: 500,
                            }}
                          >
                            {task.assigned_to_name}
                          </Typography>
                        </Stack>
                      ) : (
                        <Stack direction="row" alignItems="center" spacing={0.8}>
                          <PersonOutlineIcon sx={{ fontSize: 18, color: '#CBD5E1' }} />
                          <Typography
                            sx={{
                              fontSize: '0.8rem',
                              color: '#94A3B8',
                              fontStyle: 'italic',
                            }}
                          >
                            Unassigned
                          </Typography>
                        </Stack>
                      )}
                    </TableCell>
                    {/* Assigned By */}
                    <TableCell>
                      {task.created_by_name ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar
                            sx={{
                              width: 30,
                              height: 30,
                              background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                            }}
                          >
                            {getInitials(task.assigned_by_name || '—')}
                          </Avatar>
                          <Typography
                            sx={{
                              fontSize: '0.83rem',
                              color: '#334155',
                              fontWeight: 500,
                            }}
                          >
                            {task.assigned_by_name}
                          </Typography>
                        </Stack>
                      ) : (
                        <Stack direction="row" alignItems="center" spacing={0.8}>
                          <PersonOutlineIcon sx={{ fontSize: 18, color: '#CBD5E1' }} />
                          <Typography
                            sx={{
                              fontSize: '0.8rem',
                              color: '#94A3B8',
                              fontStyle: 'italic',
                            }}
                          >
                            Unassigned
                          </Typography>
                        </Stack>
                      )}
                    </TableCell>

                    {/* Priority */}
                    <TableCell>
                      <Chip
                        icon={React.cloneElement(priority.icon, {
                          sx: { fontSize: 14, color: `${priority.color} !important` },
                        })}
                        label={priority.label}
                        size="small"
                        sx={{
                          height: 26,
                          background: priority.bg,
                          color: priority.color,
                          border: `1px solid ${priority.border}`,
                          fontWeight: 600,
                          fontSize: '0.72rem',
                          borderRadius: '6px',
                          '& .MuiChip-icon': {
                            color: `${priority.color} !important`,
                            ml: '6px',
                          },
                          '& .MuiChip-label': { px: 0.9 },
                        }}
                      />
                    </TableCell>

                    {/* Due Date */}
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.8}>
                        <CalendarTodayIcon
                          sx={{
                            fontSize: 14,
                            color: isOverdue ? '#EF4444' : '#64748B',
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '0.82rem',
                            color: isOverdue ? '#EF4444' : '#334155',
                            fontWeight: isOverdue ? 600 : 500,
                          }}
                        >
                          {formatDate(task.expected_delivery_date)}
                        </Typography>
                      </Stack>
                    </TableCell>

                    {/* Created On */}
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.8}>
                        <AccessTimeIcon sx={{ fontSize: 14, color: '#64748B' }} />
                        <Typography
                          sx={{
                            fontSize: '0.82rem',
                            color: '#334155',
                            fontWeight: 500,
                          }}
                        >
                          {formatDate(task.created_at)}
                        </Typography>
                      </Stack>
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <Chip
                        icon={React.cloneElement(status.icon, {
                          sx: { fontSize: 14, color: `${status.color} !important` },
                        })}
                        label={task.assignment_status_name || 'Pending'}
                        size="small"
                        sx={{
                          height: 26,
                          background: status.bg,
                          color: status.color,
                          border: `1px solid ${status.border}`,
                          fontWeight: 600,
                          fontSize: '0.72rem',
                          borderRadius: '6px',
                          '& .MuiChip-icon': {
                            color: `${status.color} !important`,
                            ml: '6px',
                          },
                          '& .MuiChip-label': { px: 0.9 },
                        }}
                      />
                    </TableCell>

                    {/* Action */}
                    <TableCell sx={{ pr: 3, textAlign: 'center' }}>
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="View Details" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleView(task)}
                            sx={{
                              width: 34,
                              height: 34,
                              background: 'linear-gradient(135deg, #EEF2FF 0%, #F5F3FF 100%)',
                              color: '#6366F1',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                color: '#FFFFFF',
                                transform: 'scale(1.05)',
                              },
                            }}
                          >
                            <VisibilityIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Task" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(task)}
                            sx={{
                              width: 34,
                              height: 34,
                              background: 'linear-gradient(135deg, #FEF3C7 0%, #FEEBC8 100%)',
                              color: '#92400E',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #92400E 0%, #D97706 100%)',
                                color: '#FFFFFF',
                                transform: 'scale(1.05)',
                              },
                            }}
                          >
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Task" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(task)}
                            sx={{
                              width: 34,
                              height: 34,
                              background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)',
                              color: '#B91C1C',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                                color: '#FFFFFF',
                                transform: 'scale(1.05)',
                              },
                            }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {openDialog && selectedTask && (
        <TaskDetailsDialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          task={{ raw: selectedTask }}
          filters={filters}
          showStatusUpdate={false}
        />
      )}
      {openEditDialog && selectedTask && (
        <EditTask
          open={openEditDialog}
          setOpen={setOpenEditDialog}
          task={selectedTask}
          filters={filters}
        />
      )}

      {/* Delete confirmation */}
      <Dialog
        open={confirmDelete.open}
        onClose={closeConfirmDelete}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 20, color: '#B91C1C' }} />
          </Box>
          Delete Task
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#475569', fontSize: '0.9rem' }}>
            Are you sure you want to delete{' '}
            <strong style={{ color: '#1E293B' }}>{confirmDelete.task?.title}</strong>? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={closeConfirmDelete}
            disabled={deleteLoading}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#64748B' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={deleteLoading}
            variant="contained"
            startIcon={
              deleteLoading ? (
                <CircularProgress size={15} color="inherit" />
              ) : (
                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
              )
            }
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)' },
            }}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyTasksTable;
